import { baseApi } from '@shared/api/baseApi';

export const authApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		requestAuthToken: builder.mutation({
			// fix-admin-unverified-logout:
			// Added `email` as an accepted identifier alongside the existing `phone`.
			// - Home.jsx / LoginForm.jsx (initial login) call this with `phone` (or neither) —
			//   their behavior is unchanged, this branch still wins when `email` is absent.
			// - DebitCardsCompanyIdentModal.jsx (re-ident redirect for an unverified admin) only
			//   has the user's `email` available, and previously it was silently dropped here
			//   because this function didn't destructure it — the request body went out empty.
			// `email` takes priority when both are present; falls back to `phone`, then `{}`.
			query: ({ phone, email, redirectURL } = {}) => ({
				method: 'POST',
				url: '/v1/user/authtoken',
				body: email ? { email } : phone ? { phone } : {},
				headers: { redirectURL },
			}),

			transformResponse: (response) => {
				return { loginURL: response?.loginURL };
			},
		}),

		refreshAccessToken: builder.mutation({
			query: ({ refreshToken, userId }) => ({
				method: 'POST',
				url: '/v1/user/refreshToken',
				body: { refreshToken, userId },
			}),
		}),

		logOut: builder.mutation({
			query: (refreshToken) => ({
				method: 'POST',
				url: '/v1/user/logout',
				body: { refreshToken },
			}),
		}),
	}),
});

export const { useRequestAuthTokenMutation, useLogOutMutation } = authApi;

//! POST /v1/user/authtoken
// Auth Required
// Starts the authentication process to create an auth token. This endpoint will return
// a loginUrl to a login page for authentication. Once successfully authenticated the user
// will be redirected to provided redirect URL with the token as query parameter authtoken.
//
// Headers:
//   redirectURL: string (required)
//
// Body (application/json):
//   phone: string (required)
//
// Response 300 — The user needs to be redirected to provided loginUrl:
//   { loginURL: string }
//
//   Schema:
//   {
//     "type": "object",
//     "properties": {
//       "loginURL": { "type": "string" }
//     }
//   }

//! POST /v1/user/refreshToken
// Auth Required
// Refreshes the user's access token using a refresh token.
//
// Body (application/json):
//   refreshToken: string (required)
//   userId: string, uuid (required)
//
// Responses:
//   200 — OK (application/json)
//     {
//       "accessToken": "string",
//       "refreshToken": "string",
//       "expiresAt": "2026-06-14T08:02:55.013Z"
//     }
//
//     Schema:
//     {
//       "type": "object",
//       "properties": {
//         "accessToken": { "type": "string" },
//         "refreshToken": { "type": "string" },
//         "expiresAt":    { "type": "string", "format": "date-time" }
//       }
//     }
//
//   400 — Bad request
//   401 — Unauthorized: invalid or missing token
//   404 — Not found
//   500 — Internal server error

//! POST /v1/user/logout
// Auth Required
// Logs out the user by invalidating the current session (access and refresh token).
//
// Body (application/json):
//   refreshToken: string (optional) — if provided, the refresh token is also invalidated
//
// Responses:
//   200 — OK
//   400 — Bad request
//   401 — Unauthorized: invalid or missing token
//   404 — Not found
//   500 — Internal server error
